var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React from "react";
import { find, gt, isEmpty, isNil, map, not, propEq, takeLast } from "ramda";
import DividingLine from "components/dividing_line";
import PriceRow, { PriceRowList, } from "components/online_ordering/cart/price_row";
import Tooltip from "components/online_ordering/tooltip";
import { formatMoneyFromCents } from "helpers/money";
var mapPriceRows = map(function (_a) {
    var key = _a.key, forward = __rest(_a, ["key"]);
    return (React.createElement(PriceRow, __assign({ key: key }, forward)));
});
export default function CheckoutSummary(_a) {
    var balanceDue = _a.balanceDue, _b = _a.feeByMethod, feeByMethod = _b === void 0 ? 0 : _b, giftCards = _a.giftCards, isAwaitingCalculation = _a.isAwaitingCalculation, isShowingTip = _a.isShowingTip, promo = _a.promo, restaurantFee = _a.serviceFee, subtotal = _a.subtotal, tax = _a.tax, tip = _a.tip, total = _a.total, upserveFee = _a.upserveFee;
    var priceRows = [
        {
            key: "subtotal",
            title: "Item Subtotal",
            value: formatMoneyFromCents(subtotal),
        },
    ];
    if (promo) {
        var discount = promo.discount, percentage = promo.percentage;
        if (discount > 0) {
            priceRows.push({
                key: "promo",
                title: "Promo " + percentage + "%",
                value: "-" + formatMoneyFromCents(discount),
            });
        }
    }
    // Build taxes and fees
    var taxesAndFees = [];
    if (!isAwaitingCalculation) {
        taxesAndFees.push({
            key: "tax",
            title: gt(upserveFee, 0) ? "Sales Tax" : "Tax",
            value: formatMoneyFromCents(tax),
        });
        if (feeByMethod) {
            taxesAndFees.push({
                key: "deliveryFee",
                title: "Delivery Fee",
                value: formatMoneyFromCents(feeByMethod),
            });
        }
        if (gt(restaurantFee, 0)) {
            taxesAndFees.push({
                key: "restaurantFee",
                title: "Restaurant Fee",
                value: formatMoneyFromCents(restaurantFee),
            });
        }
    }
    if (gt(upserveFee, 0)) {
        taxesAndFees.push({
            key: "serviceFee",
            title: "Service Fee*",
            value: formatMoneyFromCents(upserveFee),
        });
        if (!isAwaitingCalculation) {
            // When Upserve Fee applies, only show taxesAndFees tooltip during checkout,
            // after calculation completes.
            priceRows.push({
                key: "taxAndFee",
                title: (React.createElement(React.Fragment, null,
                    "Taxes and Fees",
                    React.createElement(Tooltip, null,
                        React.createElement(PriceRowList, { isDense: true }, mapPriceRows(taxesAndFees)),
                        React.createElement("p", { id: "asterisk" }, "*This fee is charged by our online ordering partner, Lightspeed (U-Series), to help offset costs associated with the convenience of placing online orders.")))),
                value: formatMoneyFromCents(tax + feeByMethod + upserveFee + restaurantFee),
            });
        }
    }
    else {
        // When Upserve Fee does not apply, taxes and fees always displayed
        priceRows.push.apply(priceRows, taxesAndFees);
    }
    if (isShowingTip) {
        priceRows.push({
            key: "tip",
            title: "Tip" + (tip ? " " + Math.round((100 * tip.valueOf()) / subtotal) + "%" : ""),
            value: tip.format(),
        });
    }
    priceRows.push({
        key: "total",
        isBold: true,
        title: "Order Total",
        value: formatMoneyFromCents(total),
    });
    var paymentRows = [];
    var makeRowFromGiftCard = function (giftCard) {
        return {
            key: "gift card " + giftCard.index,
            title: (React.createElement(React.Fragment, null, "Gift Card: ..." + takeLast(4, giftCard.giftCardNumber),
                React.createElement(Tooltip, null,
                    React.createElement(PriceRowList, { isDense: true },
                        React.createElement(PriceRow, { title: "Current balance:", value: formatMoneyFromCents(giftCard.balance) }),
                        React.createElement(PriceRow, { title: "Balance after purchase:", value: formatMoneyFromCents(giftCard.remainingBalance) }))))),
            value: formatMoneyFromCents(giftCard.amount + giftCard.tipAmount),
        };
    };
    var firstGiftCard = find(propEq("index", 0), giftCards)
        ? makeRowFromGiftCard(find(propEq("index", 0), giftCards))
        : undefined;
    var secondGiftCard = find(propEq("index", 1), giftCards)
        ? makeRowFromGiftCard(find(propEq("index", 1), giftCards))
        : undefined;
    if (firstGiftCard) {
        paymentRows = __spreadArrays(paymentRows, [firstGiftCard]);
    }
    if (secondGiftCard) {
        paymentRows = __spreadArrays(paymentRows, [secondGiftCard]);
    }
    if (not(isNil(balanceDue))) {
        paymentRows = __spreadArrays(paymentRows, [
            {
                key: "balance due",
                title: "Balance Due",
                value: formatMoneyFromCents(balanceDue),
                isBold: true,
            },
        ]);
    }
    return (React.createElement("div", { className: "checkout-summary" },
        React.createElement(DividingLine, { role: "presentation" }),
        React.createElement(PriceRowList, null, mapPriceRows(priceRows)),
        !isEmpty(giftCards) && (React.createElement(React.Fragment, null,
            React.createElement(DividingLine, { role: "presentation" }),
            React.createElement(PriceRowList, null, mapPriceRows(paymentRows))))));
}
